import React, { useEffect, useRef, useState } from 'react';
import './ConsoleContextMenu.css';

// Available monospace fonts
const FONT_OPTIONS = [
    { value: 'M PLUS 1 Code', label: 'M PLUS 1 Code' },
    { value: 'Fira Code', label: 'Fira Code' },
    { value: 'JetBrains Mono', label: 'JetBrains Mono' },
    { value: 'Source Code Pro', label: 'Source Code Pro' },
    { value: 'Roboto Mono', label: 'Roboto Mono' },
    { value: 'Ubuntu Mono', label: 'Ubuntu Mono' },
    { value: 'Cascadia Code', label: 'Cascadia Code' },
    { value: 'Consolas', label: 'Consolas' },
    { value: 'Courier New', label: 'Courier New' },
];

// Font size options
const SIZE_OPTIONS = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
    { value: 'xlarge', label: 'Extra Large' },
];

function ConsoleContextMenu({ 
    x, 
    y, 
    onClose,
    showSecondConsole,
    onToggleSecondConsole,
    consoleLayout,
    onLayoutChange,
    consoleSwapped,
    onToggleSwapped,
    showTimestamps,
    onToggleTimestamps,
    consoleFontFamily,
    onFontFamilyChange,
    consoleFontSize,
    onFontSizeChange,
    onClearConsole1,
    onClearConsole2,
    onClearAllConsoles
}) {
    const menuRef = useRef(null);
    const [showFontSubmenu, setShowFontSubmenu] = useState(false);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                onClose();
            }
        };

        // Close on escape key
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    // Adjust position to keep menu within viewport
    useEffect(() => {
        if (menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let adjustedX = x;
            let adjustedY = y;

            if (x + rect.width > viewportWidth) {
                adjustedX = viewportWidth - rect.width - 10;
            }
            if (y + rect.height > viewportHeight) {
                adjustedY = viewportHeight - rect.height - 10;
            }

            menuRef.current.style.left = `${adjustedX}px`;
            menuRef.current.style.top = `${adjustedY}px`;
        }
    }, [x, y]);

    const handleToggleSecondConsole = (e) => {
        e.stopPropagation();
        onToggleSecondConsole();
    };

    const handleLayoutChange = (e, layout) => {
        e.stopPropagation();
        onLayoutChange(layout);
    };

    const handleToggleSwapped = (e) => {
        e.stopPropagation();
        onToggleSwapped();
    };

    const handleToggleTimestamps = (e) => {
        e.stopPropagation();
        onToggleTimestamps();
    };

    const handleFontFamilyChange = (e, font) => {
        e.stopPropagation();
        onFontFamilyChange(font);
        setShowFontSubmenu(false);
    };

    const handleFontSizeChange = (e, size) => {
        e.stopPropagation();
        onFontSizeChange(size);
    };

    const toggleFontSubmenu = (e) => {
        e.stopPropagation();
        setShowFontSubmenu(!showFontSubmenu);
    };

    const handleClearConsole1 = (e) => {
        e.stopPropagation();
        onClearConsole1();
    };

    const handleClearConsole2 = (e) => {
        e.stopPropagation();
        onClearConsole2();
    };

    const handleClearAll = (e) => {
        e.stopPropagation();
        onClearAllConsoles();
    };

    return (
        <div 
            ref={menuRef}
            className="console-context-menu" 
            style={{ left: `${x}px`, top: `${y}px` }}
        >
            <ul>
                {/* Second Console Toggle */}
                <li onClick={handleToggleSecondConsole} className="menu-item-checkbox">
                    <span className={`checkbox ${showSecondConsole ? 'checked' : ''}`}>
                        {showSecondConsole ? '✓' : ''}
                    </span>
                    <span>Second Console</span>
                </li>

                <li className="context-menu-divider" />

                {/* Layout Options */}
                <li 
                    onClick={(e) => showSecondConsole && handleLayoutChange(e, 'horizontal')} 
                    className={`menu-item-radio ${!showSecondConsole ? 'disabled' : ''}`}
                >
                    <span className={`radio ${consoleLayout === 'horizontal' ? 'checked' : ''}`}>
                        {consoleLayout === 'horizontal' ? '●' : '○'}
                    </span>
                    <span>Horizontal (Side by Side)</span>
                </li>
                <li 
                    onClick={(e) => showSecondConsole && handleLayoutChange(e, 'vertical')} 
                    className={`menu-item-radio ${!showSecondConsole ? 'disabled' : ''}`}
                >
                    <span className={`radio ${consoleLayout === 'vertical' ? 'checked' : ''}`}>
                        {consoleLayout === 'vertical' ? '●' : '○'}
                    </span>
                    <span>Vertical (Stacked)</span>
                </li>

                <li className="context-menu-divider" />

                {/* Swap Order */}
                <li 
                    onClick={(e) => showSecondConsole && handleToggleSwapped(e)} 
                    className={`menu-item-checkbox ${!showSecondConsole ? 'disabled' : ''}`}
                >
                    <span className={`checkbox ${consoleSwapped ? 'checked' : ''}`}>
                        {consoleSwapped ? '✓' : ''}
                    </span>
                    <span>Swap Console Order</span>
                </li>

                <li className="context-menu-divider" />

                {/* Timestamps Toggle */}
                <li onClick={handleToggleTimestamps} className="menu-item-checkbox">
                    <span className={`checkbox ${showTimestamps ? 'checked' : ''}`}>
                        {showTimestamps ? '✓' : ''}
                    </span>
                    <span>Show Timestamps</span>
                </li>

                <li className="context-menu-divider" />

                {/* Font Family Submenu */}
                <li 
                    className="menu-item-submenu"
                    onClick={toggleFontSubmenu}
                    onMouseEnter={() => setShowFontSubmenu(true)}
                >
                    <span>Font Family</span>
                    <span className="submenu-arrow">▶</span>
                    {showFontSubmenu && (
                        <ul className="font-submenu" onMouseLeave={() => setShowFontSubmenu(false)}>
                            {FONT_OPTIONS.map((font) => (
                                <li 
                                    key={font.value}
                                    onClick={(e) => handleFontFamilyChange(e, font.value)}
                                    className="menu-item-radio"
                                    style={{ fontFamily: `"${font.value}", monospace` }}
                                >
                                    <span className={`radio ${consoleFontFamily === font.value ? 'checked' : ''}`}>
                                        {consoleFontFamily === font.value ? '●' : '○'}
                                    </span>
                                    <span>{font.label}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </li>

                <li className="context-menu-divider" />

                {/* Font Size Options */}
                <li className="menu-section-header">Font Size</li>
                {SIZE_OPTIONS.map((size) => (
                    <li 
                        key={size.value}
                        onClick={(e) => handleFontSizeChange(e, size.value)}
                        className="menu-item-radio"
                    >
                        <span className={`radio ${consoleFontSize === size.value ? 'checked' : ''}`}>
                            {consoleFontSize === size.value ? '●' : '○'}
                        </span>
                        <span>{size.label}</span>
                    </li>
                ))}

                <li className="context-menu-divider" />

                {/* Clear Console Options */}
                <li className="menu-section-header">Clear Console</li>
                <li onClick={handleClearConsole1} className="menu-item">
                    <span>Clear Console 1</span>
                </li>
                <li 
                    onClick={(e) => showSecondConsole && handleClearConsole2(e)} 
                    className={`menu-item ${!showSecondConsole ? 'disabled' : ''}`}
                >
                    <span>Clear Console 2</span>
                </li>
                <li onClick={handleClearAll} className="menu-item">
                    <span>Clear All</span>
                </li>
            </ul>
        </div>
    );
}

export default ConsoleContextMenu;
